// Movement along flow fields. Pure, deterministic, no Math.random. See spec §4 (movement, suppression,
// logistics) and plan Task 8. Runs every tick for every alive unit with a destination cell.
import type { Unit, UnitType, Dials, Graphic, Posture } from './types';
import { TICK_SEC } from './types';
import type { Grid } from './grid';
import type { FlowFieldCache } from './flowfield';
import { nextCell } from './flowfield';
import type { EventLog } from './events';
import { UNIT_DEFS } from './scenario';

/** Class speed in kph, from the shared per-type unit definitions (data/units.json). */
function speedKphOf(type: UnitType): number {
  return UNIT_DEFS[type].speedKph;
}

/** Posture a unit adopts on arrival, keyed by the posture its order set it to while moving. */
export const ARRIVAL_POSTURE: Partial<Record<Posture, Posture>> = {
  move: 'hold',
  attack: 'attack',
  withdraw: 'hold',
};

const SEPARATION_RADIUS_CM = 15000; // 150 m
const SEPARATION_NUDGE_CM = 30;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

const MAX_SUBSTEPS = 64;

/**
 * Walks a unit's movement budget for this tick along the flow field, one cell-boundary "waypoint" at a
 * time (re-picking the next cell after each waypoint), so a unit faster than one cell width per tick
 * still covers the right distance instead of stalling at the first waypoint. Never overshoots the
 * destination center. Integer cm result.
 */
function walkFlowField(
  start: { x: number; y: number },
  budgetCm: number,
  destCell: number,
  destCenter: { x: number; y: number },
  isAir: boolean,
  grid: Grid,
  ffc: FlowFieldCache,
): { x: number; y: number } {
  let cur = { x: start.x, y: start.y };
  let remaining = budgetCm;
  for (let i = 0; i < MAX_SUBSTEPS && remaining > 0; i++) {
    let target = destCenter;
    if (!isAir) {
      const cell = grid.cellOfCm(cur.x, cur.y);
      const ff = ffc.get(destCell);
      const nc = nextCell(ff, grid, cell);
      target = nc === -1 ? destCenter : grid.centerCm(nc);
    }
    const dx = target.x - cur.x;
    const dy = target.y - cur.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist <= 0) break;
    const m = Math.min(remaining, dist);
    cur = { x: cur.x + Math.round((dx / dist) * m), y: cur.y + Math.round((dy / dist) * m) };
    remaining -= m;
    if (Math.hypot(cur.x - destCenter.x, cur.y - destCenter.y) <= grid.cellCm) break; // arrived, stop stepping
  }
  return cur;
}

function cross(a: { x: number; y: number }, b: { x: number; y: number }, c: { x: number; y: number }): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function onSegment(a: { x: number; y: number }, b: { x: number; y: number }, p: { x: number; y: number }): boolean {
  return Math.min(a.x, b.x) <= p.x && p.x <= Math.max(a.x, b.x) && Math.min(a.y, b.y) <= p.y && p.y <= Math.max(a.y, b.y);
}

/** Standard segment-segment intersection test (orientation + collinear-overlap cases), integer-safe. */
function segmentsIntersect(
  p1: { x: number; y: number }, p2: { x: number; y: number },
  p3: { x: number; y: number }, p4: { x: number; y: number },
): boolean {
  const d1 = cross(p3, p4, p1);
  const d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3);
  const d4 = cross(p1, p2, p4);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
  if (d1 === 0 && onSegment(p3, p4, p1)) return true;
  if (d2 === 0 && onSegment(p3, p4, p2)) return true;
  if (d3 === 0 && onSegment(p1, p2, p3)) return true;
  if (d4 === 0 && onSegment(p1, p2, p4)) return true;
  return false;
}

function handleArrival(unit: Unit, t: number, log: EventLog): void {
  unit.destCell = -1;
  unit.posture = ARRIVAL_POSTURE[unit.posture] ?? unit.posture;
  if (unit.orderId >= 0) {
    const orderId = unit.orderId;
    log.push(t, 'order.done', unit.side, { orderId }, { unitId: unit.id });
    unit.orderId = -1;
  }
}

function checkPlCrossing(
  unit: Unit,
  prev: { x: number; y: number },
  next: { x: number; y: number },
  graphics: Graphic[],
  t: number,
  log: EventLog,
): void {
  for (const g of graphics) {
    if (g.kind !== 'pl') continue;
    if (g.pts.length < 2) continue;
    if (unit.crossedPls.includes(g.id)) continue;
    let crossed = false;
    for (let i = 0; i < g.pts.length - 1; i++) {
      if (segmentsIntersect(prev, next, g.pts[i], g.pts[i + 1])) { crossed = true; break; }
    }
    if (crossed) {
      unit.crossedPls.push(g.id);
      log.push(t, 'pl.crossed', unit.side, { graphicId: g.id, name: g.name }, {
        unitId: unit.id,
        significant: unit.side === 0,
      });
    }
  }
}

/**
 * Advances every alive unit with a destination one tick along its flow field, applies separation
 * steering, handles arrival (posture change + order.done), and detects phase-line crossings.
 */
export function stepMovement(
  units: Unit[],
  grid: Grid,
  ffc: FlowFieldCache,
  dials: Dials,
  t: number,
  log: EventLog,
  graphics: Graphic[],
): void {
  // Snapshot starting positions once so separation steering is order-independent within this tick.
  const startPositions = units.map((u) => ({ x: u.x, y: u.y }));

  for (let i = 0; i < units.length; i++) {
    const unit = units[i];
    if (!unit.alive) { unit.movedCm = 0; continue; }
    if (unit.destCell === -1) { unit.movedCm = 0; continue; }

    const destCell = unit.destCell;
    const destCenter = grid.centerCm(destCell);
    const prevPos = { x: unit.x, y: unit.y };
    const isAir = unit.type === 'uas';

    const isBroken = t < unit.brokenUntil;
    const isReplanning = !isBroken && t < unit.replanUntil;

    let speedCmPerTick = 0;
    if (!isReplanning) {
      const kph = speedKphOf(unit.type);
      const base = kph * (100000 / 3600) * TICK_SEC;
      if (isBroken) {
        // Broken units flee toward destCell (set by combat.ts to the nearest friendly HQ) at full speed,
        // ignoring terrain, suppression, night, and fuel penalties.
        speedCmPerTick = base;
      } else {
        const cell = grid.cellOfCm(prevPos.x, prevPos.y);
        const costFactor = isAir ? 1 : clamp(4 / Math.max(1, grid.costAt(cell)), 0.3, 1.5);
        const suppFactor = unit.suppression > 60 ? 0 : 1 - unit.suppression / 150;
        const nightFactor = dials.tod === 'night' && !isAir ? 0.7 : 1;
        const fuelFactor = unit.fuel <= 0 ? 0 : unit.fuel < 20 ? 0.5 : 1;
        speedCmPerTick = base * costFactor * suppFactor * nightFactor * fuelFactor;
      }
    }

    let newPos = prevPos;

    if (speedCmPerTick > 0) {
      // Air units (uas) ignore terrain cost and fly straight toward the destination; everyone else
      // follows the cached flow field, re-picking the next cell as they cross cell boundaries so a
      // unit faster than one cell width per tick still covers its full budget.
      const moveCm = Math.round(speedCmPerTick);
      const stepped = walkFlowField(prevPos, moveCm, destCell, destCenter, isAir, grid, ffc);
      let nx = stepped.x;
      let ny = stepped.y;

      // Separation: nudge 30 cm/tick perpendicular to the travel direction if another alive friendly
      // is within 150 m. Direction is deterministic: unit id parity picks which side to nudge toward.
      let hasNeighbor = false;
      for (let j = 0; j < units.length; j++) {
        if (j === i) continue;
        const other = units[j];
        if (!other.alive || other.side !== unit.side) continue;
        const dx = startPositions[j].x - startPositions[i].x;
        const dy = startPositions[j].y - startPositions[i].y;
        if (Math.sqrt(dx * dx + dy * dy) < SEPARATION_RADIUS_CM) { hasNeighbor = true; break; }
      }
      if (hasNeighbor) {
        const dx = stepped.x - prevPos.x;
        const dy = stepped.y - prevPos.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 0) {
          const ux = dx / len;
          const uy = dy / len;
          const sign = unit.id % 2 === 0 ? 1 : -1;
          const px = -uy * sign;
          const py = ux * sign;
          nx += Math.round(px * SEPARATION_NUDGE_CM);
          ny += Math.round(py * SEPARATION_NUDGE_CM);
        }
      }

      newPos = { x: nx, y: ny };
    }

    unit.x = newPos.x;
    unit.y = newPos.y;
    unit.movedCm = Math.round(Math.hypot(newPos.x - prevPos.x, newPos.y - prevPos.y));

    // Arrival: within one cell of the destination center. Checked even if the unit did not move this
    // tick (e.g. it started already there), independent of how it got here.
    const distToDest = Math.hypot(newPos.x - destCenter.x, newPos.y - destCenter.y);
    if (distToDest <= grid.cellCm) {
      handleArrival(unit, t, log);
    }

    if (unit.movedCm > 0) {
      checkPlCrossing(unit, prevPos, newPos, graphics, t, log);
    }
  }
}
